import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'
import { RedirectUrls } from 'src/types/types'
import ga from 'src/utils/pixels/gtag'

export type LogisticsSearchCardVariant = 'grid' | 'empty'

interface LogisticsSearchCardProps {
  /** `grid` — compact horizontal tile injected inline among listing cards
   * (mirrors `OnDemandSearchCard`'s card-frame classes so it sits flush in
   * the results grid). `empty` — hero-sized recovery card (mirrors
   * `OnDemandRecoveryCard`) shown on a zero-results B2B-pillar search. */
  variant: LogisticsSearchCardVariant
}

/**
 * Soluciones Logísticas (3PL) cross-link card. Injected on
 * `renta-bodegas-comerciales` / `renta-naves-industriales` searches (and the
 * generic `/search` route when the WAREHOUSE/INDUSTRIAL_WAREHOUSE filter is
 * active) — see `shouldShowLogisticsCard`. Unlike `OnDemandSearchCard` this
 * card is NOT per-city: 3PL has no partner-coverage roster, so the same
 * static content (icon, copy, CTA) renders everywhere the gate passes, and
 * the CTA always points at `/soluciones-logisticas` — no scroll-restoration
 * / new-tab dance needed since there's no per-city landing URL to build.
 *
 * Copy is generic ("operadores que los ofrecen") — no coverage, pricing, or
 * commission claims. See CLAUDE.md "Code ↔ Terms ↔ Marketing ↔ Help Center".
 */
const LogisticsSearchCard = ({ variant }: LogisticsSearchCardProps) => {
  const { t } = useTranslation('common')

  const handleClick = () => {
    ga.event('logistics_card_click', { surface: variant })
  }

  if (variant === 'empty') {
    return (
      <section
        // `@container`, not viewport `md:` — this card only renders inside
        // `<EmptyState>` → `<SearchList>`, so its box is the ~357px desktop
        // rail (narrower than a phone) at a 900px viewport. Keying sizes off
        // the viewport applied the widest treatment at the tightest width.
        // Mirrors `OnDemandRecoveryCard`; see the longer note there.
        className="@container w-full overflow-hidden rounded-2xl border border-blue-500/20 bg-cream-300"
        data-testid="logistics-recovery-card"
        aria-labelledby="logistics-recovery-heading"
      >
        {/* Real warehouse photo (2026-07-14) — the icon-only band read as
            a placeholder. Decorative (alt="") — the heading + body carry
            the meaning. Provenance: public/images/stock-credits.json. */}
        <div className="relative aspect-16/10 w-full overflow-hidden bg-blue-500/10 @sm:aspect-2/1 @xl:aspect-5/2">
          <Image
            src="/images/stock/logistics-card.webp"
            alt=""
            fill
            sizes="(min-width: 900px) 45vw, 100vw"
            className="object-cover"
          />
          <p className="absolute bottom-3 left-3 rounded-full bg-ink-700/85 px-3 py-1 font-display text-[10px] tracking-[0.16em] text-white-500 uppercase backdrop-blur-sm @sm:text-[11px]">
            {t('LOGISTICS_CARD.EYEBROW')}
          </p>
        </div>

        <div className="flex flex-col gap-4 p-5 @sm:gap-5 @sm:p-6 @xl:p-8">
          <h2
            id="logistics-recovery-heading"
            className="font-display text-[22px] leading-[1.15] font-medium text-ink-700 @sm:text-[26px] @xl:text-[30px]"
          >
            {t('LOGISTICS_CARD.TITLE')}
          </h2>
          <p className="max-w-140 text-[14px] leading-relaxed text-ink-200 @sm:text-[15px]">
            {t('LOGISTICS_CARD.BODY')}
          </p>
          <Link
            href={RedirectUrls.SolucionesLogisticas}
            onClick={handleClick}
            // `w-full` + `whitespace-nowrap` below `@md`: "Conocer soluciones
            // 3PL" wrapped inside a `w-fit` pill in the narrow rail.
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-blue-500 px-6 py-3 text-base font-medium whitespace-nowrap text-white-500 transition-transform duration-200 hover:translate-x-0.5 @md:w-auto @md:self-start @md:px-7"
          >
            <span>{t('LOGISTICS_CARD.CTA')}</span>
            <i className="si si-right text-[10px]" aria-hidden="true" />
          </Link>
        </div>
      </section>
    )
  }

  return (
    <Link
      href={RedirectUrls.SolucionesLogisticas}
      onClick={handleClick}
      data-testid="logistics-search-card"
      className="group relative block h-full overflow-hidden rounded-2xl border border-ink-100/15 bg-cream-300 transition-all duration-300"
    >
      <div className="flex h-[142px] md:h-[160px]">
        {/* Real warehouse photo (2026-07-14) — mirrors listing-card imagery
            so the tile doesn't read as an ad among photo cards. Decorative
            (alt="") — the copy carries the meaning. */}
        {/* 200px at `lg:`, not `md:` — see the note in `OnDemandSearchCard`.
            All four grid tiles share this step. */}
        <div className="relative w-[130px] shrink-0 border-r border-ink-100/15 sm:w-[155px] lg:w-[200px]">
          <Image
            src="/images/stock/logistics-card.webp"
            alt=""
            fill
            sizes="200px"
            className="object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        </div>
        <div className="flex flex-1 flex-col justify-between p-3 md:p-4">
          <div className="min-w-0">
            <span className="font-display text-[10px] tracking-[0.16em] text-blue-500 uppercase md:text-xs">
              {t('LOGISTICS_CARD.EYEBROW_SHORT')}
            </span>
            <p className="mt-1 line-clamp-2 font-display text-[13px] leading-[1.2] font-medium text-ink-700 md:text-base">
              {t('LOGISTICS_CARD.TITLE')}
            </p>
            <p className="mt-1 line-clamp-2 text-[11px] leading-tight text-ink-200 md:text-xs">
              {t('LOGISTICS_CARD.BODY_SHORT')}
            </p>
          </div>
          <span className="inline-flex w-fit items-center self-start rounded-full bg-blue-500 px-3 py-1 text-xs font-medium text-white-500 md:px-3.5 md:py-1.5 md:text-sm">
            {t('LOGISTICS_CARD.CTA')}
          </span>
        </div>
      </div>
    </Link>
  )
}

export default React.memo(LogisticsSearchCard)
